import React, { useEffect, useState } from "react";

import { MenuItem, TextField, TextFieldProps } from "@mui/material";
import { RRule } from "rrule";
import { makeStyles } from "tss-react/mui";

import useDialogState from "@/hooks/useDialogState";
import { colors } from "@/theme/overrides/colors";
import { getNthDayOfWeek, month, monthday, weekday } from "@/utils/datetime";
import { maybeCall } from "@/utils/functions";
import { capitalize } from "@/utils/string";

import CustomRecurrenceDialog from "./CustomRecurrenceDialog";

type Props = TextFieldProps & {
  sendsOn?: Date;
};

const useStyles = makeStyles()({
  root: {
    "& .MuiInput-root": {
      color: colors.primary,
    },
    "& .MuiInput-underline:before": {
      border: "0 !important",
      content: '""',
    },
    "& .MuiSelect-icon": {
      color: colors.primary,
    },
  },
});

function ruleValue(rule: RRule): string {
  return rule.toString().substring(6); // removes RRULE: from start of string
}

const daily = new RRule({ freq: RRule.DAILY });
const weekdays = new RRule({
  freq: RRule.WEEKLY,
  byweekday: [RRule.MO, RRule.TU, RRule.WE, RRule.TH, RRule.FR],
});

const RepeatPicker: React.FC<Props> = ({
  sendsOn = new Date(),
  ...textFieldProps
}) => {
  const { classes, cx } = useStyles();
  const custom = useDialogState();

  const [customRule, setCustomRule] = useState<RRule | null>(null);
  const weekly = new RRule({
    freq: RRule.WEEKLY,
    byweekday: weekday(sendsOn),
  });
  const monthly = new RRule({
    freq: RRule.MONTHLY,
    byweekday: weekday(sendsOn).nth(getNthDayOfWeek(sendsOn)),
  });
  const yearly = new RRule({
    freq: RRule.YEARLY,
    bymonth: month(sendsOn),
    bymonthday: monthday(sendsOn),
  });

  useEffect(() => {
    // if `sendsOn` changed trigger change so that premade rules are still selected
    const value = textFieldProps.value;
    if (customRule != null || value == null) return;

    const valueRule = RRule.fromString(value as string);
    if (valueRule.options.freq === RRule.WEEKLY) {
      maybeCall(textFieldProps.onChange, ruleValue(weekly) as any);
    }
    if (valueRule.options.freq === RRule.MONTHLY) {
      maybeCall(textFieldProps.onChange, ruleValue(monthly) as any);
    }
    if (valueRule.options.freq === RRule.YEARLY) {
      maybeCall(textFieldProps.onChange, ruleValue(yearly) as any);
    }
  }, [sendsOn]);

  return (
    <>
      <TextField
        {...textFieldProps}
        onChange={(e) => {
          if (e.target.value != null) {
            setCustomRule(null); // clear custom rule when one of the premade is selected
            maybeCall(textFieldProps.onChange, e);
          }
        }}
        className={cx(classes.root, textFieldProps.className)}
        select
        fullWidth
        SelectProps={{
          displayEmpty: true,
        }}
      >
        <MenuItem value="">Never</MenuItem>
        {[daily, weekly, monthly, yearly, weekdays].map((rule, index) => (
          <MenuItem key={index} value={ruleValue(rule)}>
            {capitalize(rule.toText())}
          </MenuItem>
        ))}
        {!!customRule && (
          <MenuItem value={ruleValue(customRule)}>
            {capitalize(customRule.toText())}
          </MenuItem>
        )}
        <MenuItem onClick={custom.show}>Custom</MenuItem>
      </TextField>
      <CustomRecurrenceDialog
        open={custom.shown}
        onClose={custom.hide}
        onSubmit={(rule) => {
          setCustomRule(rule);
          maybeCall(textFieldProps.onChange, ruleValue(rule) as any);
        }}
        sendsOn={sendsOn}
      />
    </>
  );
};

export default RepeatPicker;
